var _ = require('lodash'),
  cache = require('../models/cache'),
  configs = require('../config/configManager'),
  CONST_GENERAL = require('../constants/generalSettings'),
  cssPrefix = configs.getSystemConfig().elementPrefix,
  endpointMonitor = require('../libraries/endpointMonitor'),
  GetConfiguration = require('../messages/DeviceFingerprinter/binEnabled'),
  htmlUtility = require('./htmlUtility'),
  log = require('../libraries/logger').getLoggerInstance('Base.DFUtility'),
  RSVP = require('rsvp'),
  storage = require('./storageUtility');

var publicMethods = {
  binCache: {},
  binCacheUtility: {
    update: function (bin, data) {
      var binEntry = publicMethods.binCacheUtility.findBinInCache(bin);
      if (binEntry === undefined) {
        binEntry = publicMethods.binCache[bin] = {};
      }
      _.merge(binEntry, data);
    },
    getProfilingDelay: function (bin) {
      var binSettings = publicMethods.binCacheUtility.findBinInCache(bin);
      if (binSettings !== undefined && 'profilingDelay' in binSettings && !_.isNaN(binSettings.profilingDelay)) {
        return binSettings.profilingDelay;
      } else {
        return configs.getSystemConfig().defaultBinProfilingDelay;
      }
    },
    waitForProfilingToComplete: function (profilingDelay) {
      return new RSVP.Promise(function (resolve) {
        if (profilingDelay === undefined || _.isNaN(profilingDelay)) {
          log.debug('invalid delay found, setting to system default value');
          profilingDelay = configs.getSystemConfig().defaultBinProfilingDelay;
        }

        if (profilingDelay > 0) {
          log.debug('wating [' + profilingDelay + ']ms for Bin profiling to complete');
          setTimeout(function () {
            log.debug('profiling wait time completed.');
            resolve();
          }, profilingDelay);
        } else {
          resolve();
        }
      });
    },
    findBinInCache: function (bin) {
      return _.find(publicMethods.binCache, function (entry, key) {
        return (key === bin || _.startsWith(key, bin) || _.startsWith(bin, key))
      });
    }
  },
  /**
   * Creates a hidden iframe and injects it into the page. It will check for an iframe with the same Id and remove it if found. This allows it to be run repeatedly without being concerned about Id collision.
   * @param {string} dfUrl - The URL to use as the iframe 'src', this should be the DFUrl with query params on a standard DF call and empty during a bin POST.
   * @returns iframe - The iframe that was created and injected into the page
   */
  renderCollectionFrame: function (dfUrl) {
    return new RSVP.Promise(function (resolve, reject) {
      var iframe;
      try {
        var iframeId = cssPrefix + 'collector',
          oldIframe = document.getElementById(iframeId),
          bin = publicMethods.getBin(),
          iframeStyles, iframeSrc;

        // INSIGHT - To keep the DOM clean, make sure we haven't already run this call once. With the addition of Bin based detection its possible this call could be triggered multiple times per page load.
        if (oldIframe !== null) {
          document.body.removeChild(oldIframe);
        }

        if (bin === undefined) {
          iframeSrc = dfUrl;
        }

        // We have a url - build iframe to inject into page
        iframe = htmlUtility.factories.iframe(iframeId, iframeSrc);
        iframe.onload = function () {
          // this would fire for the first load but won't get data until the first post
          // since the dfUrl is not in the browser yet
          endpointMonitor.captureTiming(dfUrl, true);
        }

        // Hide iframe and push off of page view
        // INSIGHT - According to MDN docs Opera .style.PROP is read only - so we must
        // modify the values indirectly: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/style
        iframeStyles = iframe.style;
        iframeStyles.display = 'none';
        iframeStyles.position = 'absolute';
        iframeStyles.top = '-1000px';
        iframeStyles.left = '-1000px';

        // Inject Iframe to page
        document.body.appendChild(iframe);

        // We only want to make DF as completed when dealing w/ a /Browser/Render endpoints
        if(dfUrl === cache.deviceFingerprinting.urls.browser){
          storage.put(CONST_GENERAL.FINGERPRINTED_TIMESTAMP, new Date());
        }
        
        if (bin !== undefined) {
          var profileDelay = publicMethods.binCacheUtility.getProfilingDelay(bin);
          return publicMethods.postBinCollectionFrame(dfUrl, iframe, bin)
            .then(function () {
              return publicMethods.binCacheUtility.waitForProfilingToComplete(profileDelay);
            })
            .then(function () {
              return publicMethods.binCacheUtility.update(bin, {
                finished: true
              });
            })
            .then(function () {
              resolve(iframe);
            })
        } else {
          resolve(iframe);
        }

      } catch (error) {
        log.error('Failed while attempting to create DF window:' + error);
        reject();
      }

      return;
    })

  },
  /**
   * Inject an iframe with a form into the page and post the Bin value over to DF
   * @param {string} formAction - The URL that should be used to post the bin value to
   * @param {node} iframe - The DOM element of the iframe the form should be injected into
   * @param {string} bin - The bin that should be sent to DF
   */
  postBinCollectionFrame: function (formAction, iframe, bin) {
    return new RSVP.Promise(function (resolve, reject) {
      try {
        var form = htmlUtility.factories.form(formAction, 'POST'),
          binInput;

        if (bin !== undefined) {
          binInput = htmlUtility.factories.input('bin', bin, 'hidden');

          form.appendChild(binInput);
          if (htmlUtility.injectFormIntoIframe(form, iframe)) {
            publicMethods.binCacheUtility.update(bin, {
              start: new Date(),
              finished: false
            });
            form.submit();

            resolve();
          } else {
            log.error("Failed to inject form into iframe");
            reject();
          }
        } else {
          log.error('Invalid bin value [' + bin + '], unable to render collect form correctly.');
          reject();
        }
      } catch (error) {
        log.error('Failed to post bin collection frame:' + error);
        reject(error);
      }
    });
  },
  /**
   * Looks for the bin in the different places it can be stored and returns the found value. If an order object is passed in the order object is assumed to be the most recent data source
   * @param {Object} orderObject - the Order object to use as a source
   * @returns {String} bin - the bin value that was found, this value can be undefined if no bin was passed
   */
  getBin: function (orderObject) {
    var bin;

    try {
      // If an order object is passed in assume the order object is the most recent data source
      if (orderObject !== undefined) {
        var binFromOrder = publicMethods.getBinFromOrderObject(orderObject);

        if (binFromOrder !== undefined) {
          bin = binFromOrder;
        } else {
          bin = cache.bin;
        }
      } else {
        // otherwise we assume the cache is the most recent source
        if (cache.bin !== undefined) {
          bin = cache.bin;
        } else {
          bin = publicMethods.getBinFromOrderObject(cache.orderObject);
        }
      }

    } catch (error) {
      log.error('Failed while finding bin' + error);
    }

    return bin;
  },
  getBinFromOrderObject: function (orderObject) {
    var bin;

    if (orderObject != undefined) { // eslint-disable-line
      if ('Consumer' in orderObject && orderObject.Consumer !== undefined) {
        if ('Account' in orderObject.Consumer && orderObject.Consumer.Account !== undefined) {
          if ('AccountNumber' in orderObject.Consumer.Account) {
            // INSIGHT - When pulling from order object we will use the entire account number for the most specific bin rules to be run
            bin = orderObject.Consumer.Account.AccountNumber;
          }
        }
      }
    }

    return bin;
  },
  /**
   * Event handler to trigger bin profiling based off of button presses in an input field
   * @param {event} event - The event object passed in when listening to a DOM event
   */
  profileBinFromInput: function (event) {
    try {
      var element = event.target,
        binValue;

      if (element !== undefined && element !== null && element.value !== undefined && element.value !== '') {
        if (element.value.length >= 6) {
          // INSIGHT - with profile as you type, we want the profiling to occur as quickly as possible, so we will wait only for the bin before running profiling. This may result in less exact bin profiling to occur
          binValue = element.value.substring(0, 6);
          // INSIGHT - We only need to profile a bin once
          var binInCache = publicMethods.binCacheUtility.findBinInCache(binValue);
          if (binInCache === undefined) {
            cache.bin = binValue;
            publicMethods.shouldBinBeProfiled(binValue)
              .then(function (binNeedProfiling) {
                if (binNeedProfiling) {
                  log.debug('starting profiling triggered from input');
                  publicMethods.renderCollectionFrame(cache.deviceFingerprinting.urls.profileBin);
                }
              });
          }
        }
      }
    } catch (error) {
      log.debug('event ' + (event != undefined ? 'was' : 'was not') + ' defined'); // eslint-disable-line eqeqeq
      log.debug('element ' + (event.target != undefined ? 'was' : 'was not') + ' found with a type of [' + event.target.type + ']'); // eslint-disable-line eqeqeq
      log.error('Exception while attempting to profile bin value from input' + error);
    }

  },
  /**
   * A helper to determine if bin profiling needs to be run or not. If it does need to be run
   * or is already running it will wait for the configured binPofilinfDelayOnStart time before
   * resolving the returned promise.
   * @param {String=} bin - Optional bin value that was collected
   * @returns {PromiseLike} - A promise that is resolved when you should move forward with the flow
   */
  ensureBinDetectionFinished: function (bin) {
    return new RSVP.Promise(function (resolve, reject) {
      try {
        var waitTime, foundBinData;

        foundBinData = publicMethods.binCacheUtility.findBinInCache(bin);

        if (foundBinData !== undefined && foundBinData.profilingRequired === true) {
          // Profiling required, check to see if we need to wait longer for the bin profiling to complete
          var now = new Date(),
            elapsedTime = now - foundBinData.start;

          waitTime = publicMethods.binCacheUtility.getProfilingDelay(bin);

          if (elapsedTime < waitTime) {
            var timeLeft = waitTime - elapsedTime;
            log.debug('Profiling already started, but required delay has not elapsed');

            return publicMethods.binCacheUtility.waitForProfilingToComplete(timeLeft)
              .then(function () {
                resolve();
              });

          } else {
            log.debug('Profiling already completed, no wait needed');
            resolve();
          }

        } else if (foundBinData !== undefined && foundBinData.profilingRequired === false) {
          log.debug('Bin found in cache but profiling is flagged as not needed.');
          resolve();

        } else {
          // INSIGHT - Bin not found so we must have just gotten the bin. Use the passed in bin to start bin detection
          cache.bin = bin;
          log.debug('New bin found, checking for to determine if profiling is needed');
          publicMethods.shouldBinBeProfiled(bin)
            .then(function (binNeedProfiling) {
              if (binNeedProfiling) {
                log.debug('Profiling not completed, starting now');
                return publicMethods.renderCollectionFrame(cache.deviceFingerprinting.urls.profileBin)
                  .then(function () {
                    resolve();
                  });

              } else {
                resolve();
              }
            })
            .catch(function () {
              // INSIGHT - If any error is encountered trying to get bin configs we should continue on with the transaction as if nothing has happened.
              resolve();
            });
        }
      } catch (error) {
        reject(error);
      }
    })
  },
  /**
   * Checks to see if a given bin has a configuration at DeviceFingerpinter. A configuration means that the bin should be profiled, otherwise the profiling step can be skipped. If any error occurs we default to profiling. The promise is rejected if a bin was not found to check eligibility.
   * The request results are stored into the binCache to we don't need to make an additional request if the same bin comes through again.
   * @param {String=} bin - The bin to check for profiling configurations. If no bin is supplied we will attempt to find the bin set in cache
   * @returns {PromiseLike<boolean>} A flag to indicate profiling should be completed
   */
  shouldBinBeProfiled: function (bin) {
    return new RSVP.Promise(function (resolve, reject) {
      try {
        if (bin === undefined) {
          if (cache.bin === undefined) {
            bin = cache.bin = publicMethods.getBin();
          } else {
            bin = cache.bin;
          }
        }

        if (bin !== undefined) {
          if (publicMethods.binCacheUtility.findBinInCache(bin) === undefined) {
            GetConfiguration.process(cache.bin, cache.orgUnitId)
              .then(function (binProfilingEnabledResponse) {
                log.debug('Bin ' + (binProfilingEnabledResponse.ConfigsEnabled ? 'requires' : 'does not require') + ' profiling.');
                publicMethods.binCacheUtility.update(bin, {
                  profilingRequired: binProfilingEnabledResponse.ConfigsEnabled,
                  profilingDelay: binProfilingEnabledResponse.ProfilingDelay
                });
                resolve(binProfilingEnabledResponse.ConfigsEnabled);
              })['catch'](function (error) {
                log.error('Get Bin Configuration failed: ' + JSON.stringify(error));
                resolve(true);
              });
          } else {
            resolve(false);
          }

        } else {
          reject('No bin found to check for profiling eligibility.');
        }

      } catch (error) {
        log.error('Failure: ' + error);
        resolve(true);
      }
    });
  },
  /**
   * Small utility to convert an object of Key value pairs into a string of query parameters for a URL.
   * The string is defaulted to '?' and each kVP is appended and url encoded. The result should be added directly to the end of
   * a URL w/o any additional changes needed
   * @param {Object} queryParams An object of key value string pairs
   * @returns {String} The query parameter string thats should be appended to a URL. The string includes the starting '?'
   */
  constructQueryParameterString: function (queryParams) {
    var queryString = '?';
    if (queryParams && typeof queryParams === 'object') {
      _.each(queryParams, function (value, key) {
        queryString += encodeURIComponent(key) + '=' + encodeURIComponent(value) + '&';
      })
      queryString += 'origin=Songbird';
    }
    return queryString;
  }
};

module.exports = publicMethods;


//////////////////
// WEBPACK FOOTER
// ./utilities/deviceFingerprinterUtility.js
// module id = 57
// module chunks = 5